import { useCallback } from "react"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

function nextMetadata(
  existing: Record<string, unknown> | null | undefined,
  seen: boolean
): Record<string, unknown> {
  return { ...(existing ?? {}), seen }
}

/**
 * Writes the offline-first seen flag directly via storage + change queue —
 * skips usePreOrderMutations' generic update path so we read each row only
 * once, batch the put, and parallelise the change-queue enqueues. Cuts the
 * "click → badge flips" lag for the chat panel.
 */
export function useMarkPreOrderSeen() {
  const { storage, changeQueue, engine } = useAISync()

  const writeSeen = useCallback(
    async (rows: Array<{ row: PreOrder; seen: boolean }>): Promise<void> => {
      if (rows.length === 0) return

      const now = new Date().toISOString()
      const updatedRows: PreOrder[] = []
      const enqueueOps: Array<Promise<unknown>> = []

      for (const { row, seen } of rows) {
        if ((row.preOrderMetadata?.seen === true) === seen) continue
        const meta = nextMetadata(row.preOrderMetadata, seen)
        updatedRows.push({
          ...row,
          preOrderMetadata: meta,
          updatedAt: now,
        })
        enqueueOps.push(
          changeQueue.enqueue("pre-orders", {
            op: "update",
            id: row.id,
            data: { preOrderMetadata: meta },
          })
        )
      }

      if (updatedRows.length === 0) return

      await Promise.all([
        storage.putMany(PRE_ORDER_TABLE, updatedRows),
        ...enqueueOps,
      ])
      void engine.pushChanges("pre-orders")
    },
    [storage, changeQueue, engine]
  )

  const markSeen = useCallback(
    async (id: string): Promise<void> => {
      const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
      if (!existing) return
      await writeSeen([{ row: existing, seen: true }])
    },
    [storage, writeSeen]
  )

  const markUnseen = useCallback(
    async (id: string): Promise<void> => {
      const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
      if (!existing) return
      await writeSeen([{ row: existing, seen: false }])
    },
    [storage, writeSeen]
  )

  const markManySeen = useCallback(
    async (ids: readonly string[]): Promise<void> => {
      if (ids.length === 0) return
      const existing = await Promise.all(
        ids.map((id) => storage.get<PreOrder>(PRE_ORDER_TABLE, id))
      )
      const rows: Array<{ row: PreOrder; seen: boolean }> = []
      for (const row of existing) {
        if (row) rows.push({ row, seen: true })
      }
      await writeSeen(rows)
    },
    [storage, writeSeen]
  )

  const markAllSeen = useCallback(async (): Promise<void> => {
    const all = await storage.getAll<PreOrder>(PRE_ORDER_TABLE)
    const rows: Array<{ row: PreOrder; seen: boolean }> = []
    for (const row of all) {
      if (row.deletedAt) continue
      rows.push({ row, seen: true })
    }
    await writeSeen(rows)
  }, [storage, writeSeen])

  return { markSeen, markUnseen, markManySeen, markAllSeen }
}
